import { ComputedRef, Ref } from "vue";
import { DateTime } from "luxon";
import { BookingForMap, DriverPositionUpdateModalParameters } from "@components/DriverPositionUpdateModalPlugin";
import { bookingColor, DEAD_COLOR, formatTime, haversine, haversineSum, accuracyClass, accuracyPct } from "./useGpsAnalytics";
import { uuid as vueUuid } from "vue-uuid";
import mapstyle from "@themes/caremaster_theme.json";

// Shape passed to addEventMarker
export interface EventMarkerInfo {
    clientName?: string;
    time?: string | null;
    closestPointTime?: string;
    gpsDistance: number;
    odometerDistance: number | null;
    pointCount: number;
    accuracyPct: number | null;
}

// Shape returned by bookingGpsStats
export interface BookingGpsStat {
    booking: BookingForMap;
    i: number;
    points: App.Models.DriverPositionUpdate[];
    gpsDistance: number;
    odometerDistance: number | null;
    bookingAccuracyPct: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const sortByTime = (pts: App.Models.DriverPositionUpdate[]) =>
    [...pts].sort((a, b) =>
        DateTime.fromISO(a.position_recorded_at).toMillis() - DateTime.fromISO(b.position_recorded_at).toMillis()
    );

export const findClosest = (iso: string, pts: App.Models.DriverPositionUpdate[]) => {
    if (!pts.length) return null;
    const t = DateTime.fromISO(iso).toMillis();
    return pts.reduce((best, p) =>
        Math.abs(DateTime.fromISO(p.position_recorded_at).toMillis() - t) <
        Math.abs(DateTime.fromISO(best.position_recorded_at).toMillis() - t) ? p : best
    );
};

// For pickup: the last GPS point at or before the event time — shows where the vehicle
// actually was when it arrived, not just the nearest point in absolute time difference.
export const findLastBefore = (iso: string, pts: App.Models.DriverPositionUpdate[]) => {
    if (!pts.length) return null;
    const t = DateTime.fromISO(iso).toMillis();
    const before = pts.filter(p => DateTime.fromISO(p.position_recorded_at).toMillis() <= t);
    return before.length ? before[before.length - 1] : pts[0];
};

// For dropoff: the first GPS point at or after the event time — shows where the vehicle
// was after completing the trip, not a point from earlier in the journey.
export const findFirstAfter = (iso: string, pts: App.Models.DriverPositionUpdate[]) => {
    if (!pts.length) return null;
    const t = DateTime.fromISO(iso).toMillis();
    const after = pts.filter(p => DateTime.fromISO(p.position_recorded_at).toMillis() >= t);
    return after.length ? after[0] : pts[pts.length - 1];
};

export const segmentPoints = (pts: App.Models.DriverPositionUpdate[], bookings: BookingForMap[]) => {
    const sorted = [...bookings]
        .filter(b => b.actual_pick_up_time)
        .sort((a, b) => DateTime.fromISO(a.actual_pick_up_time!).toMillis() - DateTime.fromISO(b.actual_pick_up_time!).toMillis());

    type Seg = { points: App.Models.DriverPositionUpdate[], color: string };
    const segments: Seg[] = [];
    let currentSeg: Seg | null = null;

    pts.forEach(point => {
        const t = DateTime.fromISO(point.position_recorded_at).toMillis();
        let color = DEAD_COLOR;
        for (let i = 0; i < sorted.length; i++) {
            const pu = DateTime.fromISO(sorted[i].actual_pick_up_time!).toMillis();
            const do_ = sorted[i].actual_drop_off_time
                ? DateTime.fromISO(sorted[i].actual_drop_off_time!).toMillis() + 5 * 60 * 1000
                : Infinity;
            if (t >= pu && t <= do_) { color = bookingColor(i); break; }
        }
        if (!currentSeg || currentSeg.color !== color) {
            const bridge = currentSeg?.points[currentSeg.points.length - 1];
            currentSeg = { points: bridge ? [bridge] : [], color };
            segments.push(currentSeg);
        }
        currentSeg.points.push(point);
    });

    return segments;
};

// ─── SVG pin icon ─────────────────────────────────────────────────────────────
const createPinSvg = (color: string, letter: string): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="50" viewBox="0 0 38 50">
        <defs><filter id="s" x="-20%" y="-10%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.28)"/>
        </filter></defs>
        <path d="M19 0C8.507 0 0 8.507 0 19c0 14.25 19 31 19 31s19-16.75 19-31C38 8.507 29.493 0 19 0z"
              fill="${color}" stroke="white" stroke-width="2.5" filter="url(#s)"/>
        <circle cx="19" cy="19" r="11" fill="rgba(255,255,255,0.22)"/>
        <text x="19" y="24" text-anchor="middle"
              font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif"
              font-size="14" font-weight="700" fill="white">${letter}</text>
    </svg>`;

export const createPinIcon = (color: string, letter: string): google.maps.Icon => ({
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(createPinSvg(color, letter)),
    scaledSize: new google.maps.Size(34, 44),
    anchor: new google.maps.Point(17, 44),
});

// ─── Info window HTML (shared between journey and booking mode) ───────────────
export const buildEventInfoHtml = (type: 'pickup' | 'dropoff', color: string, info: EventMarkerInfo): string => {
    const timeStr    = info.time             ? DateTime.fromISO(info.time).toFormat('HH:mm:ss')             : '—';
    const closestStr = info.closestPointTime ? DateTime.fromISO(info.closestPointTime).toFormat('HH:mm:ss') : '—';
    const accColor   = info.accuracyPct === null ? '#666'
        : info.accuracyPct >= 90 ? '#198754'
        : info.accuracyPct >= 75 ? '#d97706'
        : '#dc3545';

    const statCard = (label: string, value: string) =>
        `<div style="background:#f8f9fa;border-radius:6px;padding:6px 10px;">
            <div style="color:#888;font-size:10px;text-transform:uppercase;letter-spacing:.05em;margin-bottom:1px;">${label}</div>
            <div style="font-weight:700;font-size:15px;color:#222;">${value}</div>
        </div>`;

    return `<div style="min-width:240px;max-width:280px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;font-size:13px;line-height:1.5;">
        <div style="background:${color};color:white;padding:10px 14px;margin:-8px -8px 12px;border-radius:4px 4px 0 0;display:flex;align-items:center;gap:8px;">
            <span style="font-size:22px;">${type === 'pickup' ? '🟢' : '🔴'}</span>
            <div>
                <div style="font-weight:700;font-size:14px;">${type === 'pickup' ? 'Pick-up' : 'Drop-off'}</div>
                ${info.clientName ? `<div style="font-size:12px;opacity:.9;">${info.clientName}</div>` : ''}
            </div>
        </div>
        <div style="margin-bottom:4px;"><span style="color:#888;font-size:11px;">Event time</span><br><strong>${timeStr}</strong></div>
        <div style="margin-bottom:10px;color:#555;font-size:11px;">📍 Nearest GPS point: ${closestStr}</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;border-top:1px solid #eee;padding-top:10px;">
            ${statCard('GPS points', String(info.pointCount))}
            ${statCard('GPS distance', info.gpsDistance.toFixed(2) + ' km')}
            ${info.odometerDistance !== null ? statCard('Odometer dist.', info.odometerDistance.toFixed(2) + ' km') : ''}
            ${info.accuracyPct !== null
                ? `<div style="background:#f8f9fa;border-radius:6px;padding:6px 10px;">
                    <div style="color:#888;font-size:10px;text-transform:uppercase;letter-spacing:.05em;margin-bottom:1px;">GPS match</div>
                    <div style="font-weight:700;font-size:15px;color:${accColor};">${info.accuracyPct}%</div>
                   </div>`
                : ''}
        </div>
    </div>`;
};

// ─── Marker helpers ───────────────────────────────────────────────────────────
export const addDotMarker = (map: google.maps.Map, p: App.Models.DriverPositionUpdate, fillColor: string, label: string) =>
    new google.maps.Marker({
        position: new google.maps.LatLng(p.latitude, p.longitude), map, zIndex: 10,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 9, fillColor, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
        label: { text: label, color: '#fff', fontSize: '10px', fontWeight: 'bold' },
    });

export const addEventMarker = (
    map: google.maps.Map,
    p: App.Models.DriverPositionUpdate,
    color: string,
    type: 'pickup' | 'dropoff',
    info: EventMarkerInfo,
) => {
    const iw = new google.maps.InfoWindow({ content: buildEventInfoHtml(type, color, info), maxWidth: 290 });
    const m = new google.maps.Marker({
        position: new google.maps.LatLng(p.latitude, p.longitude), map, zIndex: 20,
        icon: createPinIcon(color, type === 'pickup' ? 'P' : 'D'),
    });
    m.addListener('click', () => { iw.close(); iw.open(map, m); });
};

export const addGpsMarker = (map: google.maps.Map, p: App.Models.DriverPositionUpdate) => {
    const iw = new google.maps.InfoWindow();
    const m = new google.maps.Marker({
        position: new google.maps.LatLng(p.latitude, p.longitude), map,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 4, fillColor: '#10BD85', fillOpacity: 0.7, strokeWeight: 0 },
    });
    m.addListener('click', () => {
        iw.close();
        const recordedAt  = DateTime.fromISO(p.position_recorded_at);
        const uploadedAt  = p.created_at ? DateTime.fromISO(p.created_at) : null;
        const delayMin    = uploadedAt ? Math.round(uploadedAt.diff(recordedAt, 'minutes').minutes) : null;
        const lateHtml    = delayMin !== null && delayMin >= 2
            ? `<div style="margin-top:4px;padding:3px 6px;border-radius:4px;background:${delayMin >= 10 ? '#ffe5e5' : '#fff8e1'};color:${delayMin >= 10 ? '#c0392b' : '#9a6700'};">
                ${delayMin >= 10 ? '🔴' : '⚠️'} Uploaded ${delayMin >= 60 ? Math.round(delayMin / 60) + 'h' : delayMin + 'min'} after capture<br>
                <span style="font-size:10px;">Device was likely offline during journey</span>
               </div>` : '';
        iw.setContent(`<div style="font-size:12px;line-height:1.6;max-width:220px">
            🕐 Captured: ${recordedAt.toFormat('HH:mm:ss dd/MM/yyyy')}<br>
            ${uploadedAt ? `⬆ Uploaded: ${uploadedAt.toFormat('HH:mm:ss dd/MM/yyyy')}<br>` : ''}
            🚗 ${(p?.vehicle_current_speed ?? 0).toFixed(0)} km/h<br>
            🔋 ${p.device_battery_percentage}%${p.device_is_charging ? ' ⚡' : ''}
            ${lateHtml}
        </div>`);
        iw.open(map, m);
    });
};

// ─── Composable ───────────────────────────────────────────────────────────────
export function useGpsMapRendering(
    allPoints: Ref<App.Models.DriverPositionUpdate[]>,
    journeyPoints: Ref<App.Models.DriverPositionUpdate[]>,
    parameters: Ref<DriverPositionUpdateModalParameters>,
    bookingGpsStats: ComputedRef<BookingGpsStat[]>,
) {
    const uuid = vueUuid.v1();

    const isJourneyMode = () => !parameters.value.bookingUuid && !!parameters.value.journeyUuid;

    const initMap = () => {
        const element = document.getElementById(uuid);
        if (!element || !allPoints.value.length) return;

        const center = allPoints.value[Math.floor(allPoints.value.length / 2)];
        const map = new google.maps.Map(element, {
            styles: mapstyle,
            zoom: 12,
            center: new google.maps.LatLng(center.latitude, center.longitude),
        });

        const bounds = new google.maps.LatLngBounds();
        const boundsSource = isJourneyMode() ? [...journeyPoints.value, ...allPoints.value] : allPoints.value;
        boundsSource.forEach(p => bounds.extend(new google.maps.LatLng(p.latitude, p.longitude)));
        map.fitBounds(bounds);

        isJourneyMode() ? renderJourneyMode(map) : renderBookingMode(map);
        google.maps.event.trigger(map, 'resize');
    };

    const renderJourneyMode = (map: google.maps.Map) => {
        const bookings = parameters.value.journeyBookings ?? [];
        segmentPoints(allPoints.value, bookings).forEach(seg => {
            new google.maps.Polyline({
                path: seg.points.map(p => ({ lat: p.latitude, lng: p.longitude })),
                geodesic: true, strokeColor: seg.color, strokeOpacity: 0.85, strokeWeight: 4, map,
            });
        });

        if (allPoints.value.length) {
            addDotMarker(map, allPoints.value[0], '#198754', '▶');
            addDotMarker(map, allPoints.value[allPoints.value.length - 1], '#dc3545', '⏹');
        }

        const stats = bookingGpsStats.value;
        bookings.forEach((booking, i) => {
            const color = bookingColor(i);
            const stat  = stats[i];
            const sharedInfo: Omit<EventMarkerInfo, 'time' | 'closestPointTime'> = {
                clientName: booking.clientName,
                gpsDistance: stat?.gpsDistance ?? 0,
                odometerDistance: stat?.odometerDistance ?? null,
                pointCount: stat?.points.length ?? 0,
                accuracyPct: stat?.bookingAccuracyPct ?? null,
            };
            if (booking.actual_pick_up_time) {
                const pt = findLastBefore(booking.actual_pick_up_time, allPoints.value);
                if (pt) addEventMarker(map, pt, color, 'pickup', { ...sharedInfo, time: booking.actual_pick_up_time, closestPointTime: pt.position_recorded_at });
            }
            if (booking.actual_drop_off_time) {
                const pt = findFirstAfter(booking.actual_drop_off_time, allPoints.value);
                if (pt) addEventMarker(map, pt, color, 'dropoff', { ...sharedInfo, time: booking.actual_drop_off_time, closestPointTime: pt.position_recorded_at });
            }
        });

        allPoints.value.forEach(p => addGpsMarker(map, p));
    };

    const renderBookingMode = (map: google.maps.Map) => {
        if (journeyPoints.value.length > 1) {
            new google.maps.Polyline({
                path: journeyPoints.value.map(p => ({ lat: p.latitude, lng: p.longitude })),
                geodesic: true, strokeColor: '#5BA4F5', strokeOpacity: 0.75, strokeWeight: 3, map,
            });
        }
        if (allPoints.value.length > 1) {
            new google.maps.Polyline({
                path: allPoints.value.map(p => ({ lat: p.latitude, lng: p.longitude })),
                geodesic: true, strokeColor: '#007AF8', strokeOpacity: 0.9, strokeWeight: 4, map,
            });
        }

        const booking = parameters.value.booking;
        if (booking) {
            const bookingStat = {
                gpsDistance: haversineSum(allPoints.value),
                odometerDistance: (booking.origin_odometer != null && booking.destination_odometer != null)
                    ? booking.destination_odometer - booking.origin_odometer : null,
                pointCount: allPoints.value.length,
                accuracyPct: null as number | null,
            };
            bookingStat.accuracyPct = accuracyPct(bookingStat.gpsDistance, bookingStat.odometerDistance);

            const sharedInfo: Omit<EventMarkerInfo, 'time' | 'closestPointTime'> = {
                clientName: booking.clientName,
                ...bookingStat,
            };

            // Use the full journey point set for anchoring pickup/dropoff pins.
            // The booking window may have only one GPS point, which would cause both
            // P and D to resolve to the same coordinate and one pin to hide the other.
            // Journey points are denser, so pickup resolves to a point before/near the
            // event and dropoff to a point after/near the event.
            const searchPool = journeyPoints.value.length ? journeyPoints.value : allPoints.value;

            if (booking.actual_pick_up_time) {
                const pt = findLastBefore(booking.actual_pick_up_time, searchPool);
                if (pt) addEventMarker(map, pt, '#28a745', 'pickup', { ...sharedInfo, time: booking.actual_pick_up_time, closestPointTime: pt.position_recorded_at });
            }
            if (booking.actual_drop_off_time) {
                const pt = findFirstAfter(booking.actual_drop_off_time, searchPool);
                if (pt) addEventMarker(map, pt, '#dc3545', 'dropoff', { ...sharedInfo, time: booking.actual_drop_off_time, closestPointTime: pt.position_recorded_at });
            }
        }

        // No ▶ / ⏹ dot markers — the P and D pins already mark the start and end of the booking
        allPoints.value.forEach(p => addGpsMarker(map, p));
    };

    return { uuid, initMap };
}
